import { ResourceType } from '@cotera/api';
import type { UIMessage } from 'ai';
import {
  collectWorkspaceResourceRefs,
  type WorkspaceResourceRef,
} from '../dg/document/collect-workspace-resource-refs';
import type { DocumentState } from '../dg/document/document-types';
import type { WorkspaceRenderBundle } from '../dg/document/workspace-render-bundle';
import {
  formatAgentResourceAsToolLine,
  formatDatasetMentionMarkdown,
} from '../dg/document/workspace-mention-branch';
import type { Agent } from './agent';
import type { Dataset } from '../dataset/dataset';
import type { Document } from './document';
import type { Folder, FolderItemRef } from './folder';

export type WorkspaceDomainLoader = {
  readonly loadAgent: (id: string) => Promise<Agent | null>;
  readonly loadDocument: (id: string) => Promise<Document | null>;
  readonly loadFolder: (id: string) => Promise<Folder | null>;
  readonly loadDataset: (id: string) => Promise<Dataset | null>;
};

/** Use when building {@link Agent} / {@link Document} without a workspace graph (e.g. client stubs). */
export const noopWorkspaceDomainLoader: WorkspaceDomainLoader = {
  loadAgent: async () => null,
  loadDocument: async () => null,
  loadFolder: async () => null,
  loadDataset: async () => null,
};

/** Sync render bundle with no resolved workspace resources (plain prompt body only). */
export const emptyWorkspaceRenderBundle: WorkspaceRenderBundle = {
  agents: new Map(),
  documents: new Map(),
  folders: new Map(),
  datasets: new Map(),
  documentAppendix: [],
};

/** The prompt declaration for a mentioned dataset: name, id, and its current columns. */
function formatDatasetDeclaration(dataset: Dataset): string {
  return formatDatasetMentionMarkdown({
    name: dataset.name,
    id: dataset.id,
    columns: dataset.currentArtifact()?.metadata?.schema.columns ?? [],
  });
}

function formatFolderTreeLine(folder: Folder): string {
  return `📁 ${folder.name} (id: ${folder.id})`;
}

/**
 * Renders a folder and its members as a text tree using `├──` / `└──` / `│`
 * so the LLM sees an explicit hierarchy (not only two-space indent).
 *
 * @param headPrefix — characters before the `📁` on this folder's title line
 *   (empty at the root of a mention; e.g. `└── ` when this folder is the last child).
 * @param verticalPrefix — continuation for child rows (pipes/spaces under ancestors).
 */
async function formatFolderSubtree(
  folder: Folder,
  loader: WorkspaceDomainLoader,
  path: Set<string>,
  headPrefix: string,
  verticalPrefix: string
): Promise<string[]> {
  if (path.has(folder.id)) {
    return [
      `${headPrefix}${formatFolderTreeLine(folder)} (recursive reference)`,
    ];
  }
  path.add(folder.id);
  const lines: string[] = [`${headPrefix}${formatFolderTreeLine(folder)}`];
  const items = [...(await folder.items.get())].sort(
    (a, b) => a.position - b.position
  );
  for (let i = 0; i < items.length; i++) {
    const item = items[i]!;
    const isLast = i === items.length - 1;
    const branch = verticalPrefix + (isLast ? '└── ' : '├── ');
    const nextVertical = verticalPrefix + (isLast ? '    ' : '│   ');
    lines.push(
      ...(await formatFolderMemberLines(
        item,
        loader,
        path,
        branch,
        nextVertical
      ))
    );
  }
  path.delete(folder.id);
  return lines;
}

async function formatFolderMemberLines(
  item: FolderItemRef,
  loader: WorkspaceDomainLoader,
  path: Set<string>,
  headPrefix: string,
  verticalPrefix: string
): Promise<string[]> {
  switch (item.entityType) {
    case ResourceType.Folder: {
      const folder = await loader.loadFolder(item.entityId);
      if (folder === null) {
        return [`${headPrefix}📁 (missing, id: ${item.entityId})`];
      }
      return formatFolderSubtree(
        folder,
        loader,
        path,
        headPrefix,
        verticalPrefix
      );
    }
    case ResourceType.Document: {
      const doc = await loader.loadDocument(item.entityId);
      const title = doc?.displayTitle() ?? item.entityId;
      return [`${headPrefix}📄 ${title}`];
    }
    case ResourceType.Agent: {
      const agent = await loader.loadAgent(item.entityId);
      const title = agent?.title() ?? item.entityId;
      return [`${headPrefix}🤖 ${title}`];
    }
    case ResourceType.Dataset: {
      const dataset = await loader.loadDataset(item.entityId);
      const name = dataset?.name ?? item.entityId;
      return [`${headPrefix}📊 ${name}`];
    }
    default:
      return [`${headPrefix}• ${item.entityType} ${item.entityId}`];
  }
}

/**
 * All workspace resource refs reachable from a TipTap root, including refs inside
 * nested documents (loads document bodies via {@link WorkspaceDomainLoader.loadDocument}).
 */
/**
 * Every `editor-state` part root from chat messages (rich user / workspace prompt bodies).
 * Used with {@link buildReferencedDocumentsEditorStateMap} or
 * {@link Conversation.fromChatMessagesAsync} to resolve nested document @mentions.
 */
export function collectEditorStateRootsFromMessages(
  messages: ReadonlyArray<{ uiMessage: UIMessage }>
): DocumentState[] {
  const roots: DocumentState[] = [];
  for (const { uiMessage } of messages) {
    for (const part of uiMessage.parts ?? []) {
      const p = part as { type?: string; editorState?: unknown };
      if (
        p.type === 'editor-state' &&
        p.editorState !== undefined &&
        p.editorState !== null
      ) {
        roots.push(p.editorState as DocumentState);
      }
    }
  }
  return roots;
}

export async function collectRefsTransitive(
  root: DocumentState,
  loader: WorkspaceDomainLoader
): Promise<readonly WorkspaceResourceRef[]> {
  const byKey = new Map<string, WorkspaceResourceRef>();
  const queue: DocumentState[] = [root];
  const scannedDocIds = new Set<string>();

  while (queue.length > 0) {
    const state = queue.pop();
    if (state === undefined) {
      break;
    }
    const refs = collectWorkspaceResourceRefs(state);
    for (const ref of refs) {
      const key = `${ref.resourceType}:${ref.resourceId}`;
      if (!byKey.has(key)) {
        byKey.set(key, ref);
      }
      if (ref.resourceType === ResourceType.Document) {
        if (!scannedDocIds.has(ref.resourceId)) {
          scannedDocIds.add(ref.resourceId);
          const doc = await loader.loadDocument(ref.resourceId);
          if (doc !== null) {
            queue.push(doc.editorState.getState());
          }
        }
      }
    }
  }
  return [...byKey.values()];
}

function documentDependencyIds(state: DocumentState): readonly string[] {
  const out = new Set<string>();
  for (const ref of collectWorkspaceResourceRefs(state)) {
    if (ref.resourceType === ResourceType.Document) {
      out.add(ref.resourceId);
    }
  }
  return [...out];
}

function topologicalDocumentIds(
  docIds: readonly string[],
  edges: ReadonlyMap<string, readonly string[]>
): string[] {
  const idSet = new Set(docIds);
  const indegree = new Map<string, number>();
  for (const id of docIds) {
    const n = (edges.get(id) ?? []).filter((d) => idSet.has(d)).length;
    indegree.set(id, n);
  }
  const queue = [...docIds].filter((id) => (indegree.get(id) ?? 0) === 0);
  const out: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift();
    if (id === undefined) {
      break;
    }
    out.push(id);
    for (const other of docIds) {
      const deps = edges.get(other) ?? [];
      if (!deps.includes(id)) {
        continue;
      }
      const next = (indegree.get(other) ?? 0) - 1;
      indegree.set(other, next);
      if (next === 0) {
        queue.push(other);
      }
    }
  }
  if (out.length < docIds.length) {
    for (const id of docIds) {
      if (!out.includes(id)) {
        out.push(id);
      }
    }
  }
  return out;
}

/**
 * Editor states for every document reachable from the given roots (including nested
 * document-to-document mentions), keyed by document id. Pass to
 * {@link ConversationParams.referencedDocumentsById} so {@link Conversation.getLoadedTools}
 * picks up tools @-mentioned inside nested documents. Prefer {@link Conversation.fromChatMessagesAsync}
 * when you already pass {@link ConversationParams.workspaceDomainLoader} so the map is built for you.
 */
export async function buildReferencedDocumentsEditorStateMap(
  roots: readonly DocumentState[],
  loader: WorkspaceDomainLoader
): Promise<Map<string, DocumentState>> {
  const byId = new Map<string, DocumentState>();
  for (const root of roots) {
    const refs = await collectRefsTransitive(root, loader);
    for (const ref of refs) {
      if (ref.resourceType !== ResourceType.Document) {
        continue;
      }
      if (byId.has(ref.resourceId)) {
        continue;
      }
      const doc = await loader.loadDocument(ref.resourceId);
      if (doc !== null) {
        byId.set(ref.resourceId, doc.editorState.getState());
      }
    }
  }
  return byId;
}

/**
 * Loads workspace resources referenced from a TipTap root and builds a
 * {@link WorkspaceRenderBundle} for {@link WorkspacePromptEditor}.
 * Referenced document metadata is resolved for mention rendering; document bodies
 * are intentionally not inlined into prompts.
 */
export async function buildWorkspaceRenderBundle(
  root: DocumentState,
  loader: WorkspaceDomainLoader
): Promise<WorkspaceRenderBundle> {
  const refs = await collectRefsTransitive(root, loader);

  const agents = new Map<string, string>();
  const folders = new Map<string, string>();
  const datasets = new Map<string, string>();
  const loadedDocuments = new Map<string, Document>();

  const docIdSet = new Set<string>();
  for (const ref of refs) {
    if (ref.resourceType === ResourceType.Document) {
      docIdSet.add(ref.resourceId);
    }
  }

  await Promise.all(
    refs.map(async (ref) => {
      switch (ref.resourceType) {
        case ResourceType.Agent: {
          const agent = await loader.loadAgent(ref.resourceId);
          if (agent !== null) {
            agents.set(ref.resourceId, formatAgentResourceAsToolLine(agent.id));
          }
          break;
        }
        case ResourceType.Folder: {
          const folder = await loader.loadFolder(ref.resourceId);
          if (folder !== null) {
            const treeLines = await formatFolderSubtree(
              folder,
              loader,
              new Set(),
              '',
              ''
            );
            folders.set(ref.resourceId, treeLines.join('\n'));
          }
          break;
        }
        case ResourceType.Document: {
          const doc = await loader.loadDocument(ref.resourceId);
          if (doc !== null) {
            loadedDocuments.set(ref.resourceId, doc);
          }
          break;
        }
        case ResourceType.Dataset: {
          const dataset = await loader.loadDataset(ref.resourceId);
          if (dataset !== null) {
            datasets.set(ref.resourceId, formatDatasetDeclaration(dataset));
          }
          break;
        }
        default:
          break;
      }
    })
  );

  const docIds = [...docIdSet];
  const edges = new Map<string, readonly string[]>();
  for (const id of docIds) {
    const doc = loadedDocuments.get(id);
    if (doc === undefined) {
      edges.set(id, []);
    } else {
      edges.set(id, documentDependencyIds(doc.editorState.getState()));
    }
  }

  const orderedDocIds = topologicalDocumentIds(docIds, edges);
  const documents = new Map<
    string,
    { readonly title: string; readonly body: string }
  >();

  for (const id of orderedDocIds) {
    const doc = loadedDocuments.get(id);
    if (doc === undefined) {
      continue;
    }
    const title = doc.displayTitle() ?? '';
    documents.set(id, { title, body: '' });
  }

  return {
    agents,
    folders,
    datasets,
    documents,
    documentAppendix: [],
  };
}
